import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customerGroupsApi, customersApi, memberTiersApi } from "./client";

// --- Customers ---
export const customerKeys = {
  all: ["customers"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["customers", "list", q] as const,
  detail: (id: string) => ["customers", "detail", id] as const,
};

export function useCustomersQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: customerKeys.list(q),
    queryFn: () => customersApi.list(q),
  });
}

export function useCustomerQuery(id: string) {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => customersApi.get(id),
    enabled: !!id,
  });
}

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        memberTierId?: string | null;
        metadata?: Record<string, unknown> | null;
      };
    }) => customersApi.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: customerKeys.detail(id) });
      qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

// --- Member Tiers ---
export const memberTierKeys = {
  all: ["customers", "tiers"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["customers", "tiers", "list", q] as const,
  detail: (id: string) => ["customers", "tiers", "detail", id] as const,
};

export function useMemberTiersQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q = {
    limit: query.limit ?? 100,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: memberTierKeys.list(q),
    queryFn: () => memberTiersApi.list(q),
  });
}

export function useMemberTierQuery(id: string) {
  return useQuery({
    queryKey: memberTierKeys.detail(id),
    queryFn: () => memberTiersApi.get(id),
    enabled: !!id,
  });
}

export function useCreateMemberTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: { lo?: string; th?: string; en?: string };
      sortOrder: number;
    }) => memberTiersApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: memberTierKeys.all });
    },
  });
}

export function useUpdateMemberTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        name?: { lo?: string; th?: string; en?: string };
        sortOrder?: number;
      };
    }) => memberTiersApi.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: memberTierKeys.detail(id) });
      qc.invalidateQueries({ queryKey: memberTierKeys.all });
    },
  });
}

export function useDeleteMemberTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => memberTiersApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: memberTierKeys.all });
      qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

// --- Customer Groups ---
export const customerGroupKeys = {
  all: ["customers", "groups"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["customers", "groups", "list", q] as const,
  detail: (id: string) => ["customers", "groups", "detail", id] as const,
  forCustomer: (customerId: string) =>
    ["customers", "groups", "for-customer", customerId] as const,
};

export function useCustomerGroupsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q = {
    limit: query.limit ?? 100,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: customerGroupKeys.list(q),
    queryFn: () => customerGroupsApi.list(q),
  });
}

export function useCustomerGroupQuery(id: string) {
  return useQuery({
    queryKey: customerGroupKeys.detail(id),
    queryFn: () => customerGroupsApi.get(id),
    enabled: !!id,
  });
}

export function useCustomerGroupsForCustomerQuery(customerId: string) {
  return useQuery({
    queryKey: customerGroupKeys.forCustomer(customerId),
    queryFn: () => customerGroupsApi.listForCustomer(customerId),
    enabled: !!customerId,
  });
}

export function useCreateCustomerGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: { lo?: string; th?: string; en?: string };
      description?: string;
      color?: string;
      sortOrder?: number;
    }) => customerGroupsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerGroupKeys.all });
    },
  });
}

export function useUpdateCustomerGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        name?: { lo?: string; th?: string; en?: string };
        description?: string | null;
        color?: string | null;
        sortOrder?: number;
      };
    }) => customerGroupsApi.update(id, data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: customerGroupKeys.detail(id) });
      qc.invalidateQueries({ queryKey: customerGroupKeys.all });
    },
  });
}

export function useDeleteCustomerGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => customerGroupsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerGroupKeys.all });
    },
  });
}

export function useAssignCustomerToGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, groupId }: { customerId: string; groupId: string }) =>
      customerGroupsApi.assignCustomer(customerId, groupId),
    onSuccess: (_, { customerId }) => {
      qc.invalidateQueries({ queryKey: customerGroupKeys.forCustomer(customerId) });
      qc.invalidateQueries({ queryKey: customerGroupKeys.all });
    },
  });
}

export function useRemoveCustomerFromGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, groupId }: { customerId: string; groupId: string }) =>
      customerGroupsApi.removeCustomer(customerId, groupId),
    onSuccess: (_, { customerId }) => {
      qc.invalidateQueries({ queryKey: customerGroupKeys.forCustomer(customerId) });
      qc.invalidateQueries({ queryKey: customerGroupKeys.all });
    },
  });
}
